/**
 * AXORA Copilot API Client (Enrollment-aware RAG)
 * 
 * Provides helpers for the React app to communicate with the chatbot service.
 * 
 * FEATURES:
 * - Enrollment-based access control (students only see their course materials)
 * - Real-time embedding updates when educators add content
 * - Context-aware responses about courses, assignments, deadlines
 * - Automatic authentication via JWT tokens
 */

// Use Vercel API in production, localhost in development
const RAG_API_URL = process.env.NODE_ENV === 'production'
  ? '' // Use relative URLs in production (Vercel serverless functions)
  : (process.env.REACT_APP_RAG_API_URL || process.env.REACT_APP_MCP_API_URL || 'http://localhost:5001');

// Debug: show the resolved RAG API base in non-production (helps diagnose 404/Failed to fetch)
if (process.env.NODE_ENV !== 'production') {
  // eslint-disable-next-line no-console
  console.log('[ragChatbot] RAG_API_URL =', RAG_API_URL);
}

/**
 * Get authentication token from localStorage
 * @returns {string|null} JWT token
 */
const getAuthToken = () => {
  return localStorage.getItem('token');
};

/**
 * Get authorization headers for authenticated requests
 * @returns {Object} Headers object with authorization
 */
const getAuthHeaders = () => {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

/**
 * Check the readiness of the RAG system (verifies all dependencies)
 * @returns {Promise<Object>} Readiness information with dependency checks
 */
export const checkStatus = async () => {
  try {
    const url = `${RAG_API_URL}/api/status`;
    // eslint-disable-next-line no-console
    console.log('[ragChatbot] checkStatus ->', url);
    const response = await fetch(url, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      const data = await response.json().catch(() => ({ status: 'error' }));
      return data || { status: 'error' };
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Readiness check error:', error);
    return { status: 'error', error: error.message };
  }
};

/**
 * Send a query to the RAG chatbot with enrollment-based filtering
 * 
 * The chatbot automatically:
 * - Filters results to only show materials from enrolled courses
 * - Understands course context (assignments, deadlines, materials)
 * - Provides personalized responses based on student's enrollment
 * 
 * @param {string} query - The question to ask
 * @param {Array} chatHistory - Optional previous messages
 * @param {Array} images - Optional array of base64-encoded images or image URLs
 * @returns {Promise<Object>} Response with answer and sources
 * 
 * Example queries:
 * - "What's in my BIM syllabus?"
 * - "When is my next assignment due?"
 * - "Explain sustainable architecture from my course materials"
 * - "What courses am I taking?"
 * 
 * Returns: { response: "...", sources: [...] }
 */
export const chatWithRAG = async (query, chatHistory = [], images = []) => {
  try {
    const url = `${RAG_API_URL}/api/chat`;
    // Debug log: don't print auth token, only URL and query metadata
    // eslint-disable-next-line no-console
    console.log('[ragChatbot] POST', url, 'query:', query, 'history_length:', (chatHistory || []).length, 'images:', (images || []).length);

    const response = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ query, chat_history: chatHistory, images }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('RAG Chatbot error:', error);
    throw error;
  }
};

/**
 * Test the connection to the RAG API
 * @returns {Promise<boolean>} True if connected, false otherwise
 */
export const testConnection = async () => {
  try {
    const response = await fetch(`${RAG_API_URL}/`);
    
    if (!response.ok) {
      return false;
    }
    
    const data = await response.json();
    return data.status === 'healthy';
  } catch (error) {
    console.error('Connection test failed:', error);
    return false;
  }
};

const ragChatbotClient = {
  checkStatus,
  chatWithRAG,
  testConnection,
};

export default ragChatbotClient;
